import React from "react"; //{ useState, useEffect, useRef }
import { motion } from "framer-motion";
import { useHistory } from "react-router-dom";
import Circles from "../../../assets/aboutImages/circles.png";
import Semicircles from "../../../assets/aboutImages/semicircles.png";
import Squares from "../../../assets/aboutImages/squares.png";
import Controller from "../../../assets/aboutImages/controller.png";
import ResumeIcon from "../../../assets/aboutImages/resumeIcon.png";
import UpIcon from "../../../assets/aboutImages/up-icon.svg";
import GenomeIcon from "../../../assets/aboutImages/genome.png";
import styled from "styled-components";
import { isMobile } from "react-device-detect";
import FeaturedProjectsMenu from "./FeaturedProjectsMenu";
import ExperienceMenu from "./ExperienceMenu";
import useWindowSize from "./useWindowSize";

import { contentVariants, useIsFirstLoad } from "../../variants";

const Wrapper = styled(motion.div)`
  maxwidth: var(--maxWidth);
  background: var(--black);
  margin: 0 auto;
  overflow: auto;
`;

const Content = styled(motion.div)`
  overflow: auto;
`;

const Container = styled(motion.div)`
  height: 100vh;
  width: 100vw;
  background: var(--black);
  margin: 0 auto;
  position: relative;
  margin-bottom: 0;
  overflow-y: auto;
  scroll-snap-type: y mandatory;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 600px) {
    scroll-snap-type: none;
  }
  padding-top: env(safe-area-inset-top);
  padding-bottom: env(safe-area-inset-bottom);
`;

const PageName = styled(motion.h1)`
  position: relative;
  font-size: clamp(24px, 5vmax, 54px);
  margin-right: 2vw;
  ${(props) => {
    return `background-image: linear-gradient(to bottom right, ${
      colors[props.pos][0]
    }, ${colors[props.pos][1]});`;
  }}
  background-clip: text;
  text-fill-color: transparent;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const colors = {
  top: ["gold", "magenta"],
  right: ["magenta", "cyan"],
  bot: ["cyan", "green"],
  left: ["#7b4397 0%, #7b4397 25%", "#dc2430 75%, #dc2430 100%"],
};

const Row = styled(motion.div)`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;

  @media (max-width: 600px) {
    justify-content: flex-start;
    align-items: flex-start;
  }
`;

const NarrowRowContainer = styled(motion.div)`
  display: flex;
  flex-direction: row;
  justify-content: space-evenly;
  width: 85%;

  @media (max-width: 600px) {
    width: 100%;
    padding: 0 16px;
  }
`;

const Text = styled(motion.text)`
  font-family: Outfit;
  font-size: 22px;
  font-weight: 300;
  color: #fcfafb;
  opacity: ${(props) => props.opacity ?? 1};

  @media (max-width: 600px) {
    font-size: 16px;
  }
`;

const Heading = styled(motion.text)`
  font-family: Inter;
  font-weight: bold;
  font-size: 48px;
  color: white;
  opacity: 1;

  @media (max-width: 600px) {
    font-size: 28px;
  }
`;

const Subheading = styled(Heading)`
  color: #fcfafa;
  opacity: 0.7;
`;

const Divider = styled(motion.hr)`
  width: 100%;
  background-color: #d9d9d9;
  opacity: 0.2;
  border: none;
  border: 1px solid #d9d9d9;
  border-radius: 7px 7px 7px 7px;

  @media (max-width: 600px) {
    display: none;
  }
`;

const Image = styled(motion.img)`
  width: ${(props) => props.widthPercent ?? 30}%;
  height: 90%;
  flex: 1;
  object-fit: cover;
  transition-duration: 0.5s;
`;

const SpanPink = styled(motion.span)`
  font-family: inherit;
  font-size: inherit;
  font-weight: inherit;
  color: #fd71cd;
  transition-duration: 0.1s;
  cursor: pointer;

  &:hover {
    color: #e33daa;
  }
`;

const SpanRainbow = styled(motion.span)`
  font-family: inherit;
  font-size: inherit;
  font-weight: ${(props) => {
    if (props.bold) return "500;";
    else return "inherit;";
  }};

  ${(props) => {
    let gradientColors = props.colors ?? colors["top"];
    let start = props.start ?? "top left";
    return `background: -webkit-linear-gradient(${start}, ${gradientColors[0]}, ${gradientColors[1]});`;
  }}
  background-clip: text;
  text-fill-color: transparent;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Spacer = styled(motion.div)`
  height: ${(props) => props.height ?? 20}px;
  scroll-snap-align: none;

  @media (max-width: 600px) {
    height: ${(props) => Math.round((props.height ?? 20) / 3)}px;

    &.desktop-spacer {
      height: 0;
    }
  }
`;

const SectionDivider = styled(motion.div)`
  display: none;

  @media (max-width: 600px) {
    display: block;
    width: 85%;
    margin: 40px auto;
    height: 1px;
    background-color: #d9d9d9;
    opacity: 0.2;
    border-radius: 7px;
  }
`;

const HeaderBar = styled(motion.div)`
  padding-top: 30px;
  width: 100%;
  height: 16vh;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const HeaderIconsContainer = styled(motion.div)`
  display: flex;
  flex-direction: row;
`;

const ControllerImageAnimatedContainer = styled(motion.div)`
  height: clamp(51px, 6vmax, 90px);
  width: clamp(51px, 6vmax, 90px);
  border-radius: 50px;
  margin-left: 2vw;
  display: flex;
  justify-content: center;
  align-items: center;

  @keyframes ssg {
    0% {
      transform: rotate(0deg), scale(1);
    }
    25% {
      transform: rotate(360deg) scale(0.8);
    }
    50% {
      transform: rotate(360deg) scale(1.2);
    }
    75% {
      transform: rotate(360deg) scale(1);
    }
    100% {
      transform: rotate(0deg) scale(1);
    }
  }

  &:hover {
    cursor: pointer;
    animation: ssg 1.5s ease;
  }
`;

const IconAnimatedContainer = styled(motion.div)`
  height: clamp(51px, 6vmax, 90px);
  width: clamp(51px, 6vmax, 90px);
  border-radius: 50px;
  margin-left: -10px;
  display: flex;
  justify-content: center;
  align-items: center;
  transition-duration: 0.2s;
  transform: scale(1.2);

  &:hover {
    cursor: pointer;
    transform: scale(1.4);
  }
`;

const ControllerImage = styled(motion.img)`
  height: 50%;
  object-fit: cover;
`;

const About = () => {
  const windowSize = useWindowSize();
  let align = "center";

  const history = useHistory();
  const isFirstLoad = useIsFirstLoad();
  return (
    <Wrapper>
      <Content
        variants={contentVariants}
        initial={isFirstLoad ? false : "hidden"}
        animate="visible"
        exit="exit"
      >
        <Container id="scroller">
          <div style={{ scrollSnapAlign: align }}>
            <HeaderBar style={{ scrollSnapAlign: "none" }}>
              <HeaderIconsContainer>
                <ControllerImageAnimatedContainer
                  onClick={() => history.push("/joystick")}
                >
                  <ControllerImage src={Controller} alt="joystick" />
                </ControllerImageAnimatedContainer>

                <IconAnimatedContainer
                  onClick={() => window.open("/resume", "_blank")}
                >
                  <ControllerImage src={ResumeIcon} alt="resume" />
                </IconAnimatedContainer>
                <IconAnimatedContainer
                  onClick={() => window.open("/genome", "_blank")}
                >
                  <ControllerImage
                    style={{ transform: "rotate(45deg)" }}
                    src={GenomeIcon}
                    alt="genome"
                  />
                </IconAnimatedContainer>
              </HeaderIconsContainer>
              <PageName pos={"top"}>./about</PageName>
            </HeaderBar>
            <Spacer height={60} />
            <Title windowSize={windowSize} />
          </div>

          <Spacer height={300} className="desktop-spacer" />
          <SectionDivider />

          <div style={{ scrollSnapAlign: align }}>
            <Experiences />
          </div>

          <Spacer height={400} className="desktop-spacer" />
          <SectionDivider />

          <div style={{ scrollSnapAlign: align }}>
            <FeaturedProjects />
          </div>

          <div style={{ scrollSnapAlign: align }}>
            <Links />
          </div>
        </Container>
      </Content>
    </Wrapper>
  );
};

const TitleContainer = styled(motion.div)`
  display: flex;
  flex: 2;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`;

const TitleTextContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
`;

const Title = ({ windowSize }) => {
  const isSmallDevice = isMobile || windowSize.width < 500;
  return (
    <Row>
      <NarrowRowContainer>
        <TitleContainer>
          <TitleTextContainer style={{ width: isSmallDevice ? "80%" : "60%" }}>
            <Text>Hi, my name is</Text>
            <Spacer />
            <Heading>Andras Zolyomi</Heading>
            <Subheading>I build hard solutions.</Subheading>
            <Spacer height={40} />
            <Text opacity={0.96}>
              I'm a full-stack software engineer specialized in building both{" "}
              <SpanRainbow colors={["#61dafb", "gold"]} bold>
                UI products
              </SpanRainbow>{" "}
              and{" "}
              <SpanRainbow colors={["#61dafb", "gold"]} bold>
                performant safety systems
              </SpanRainbow>
              , from the ground up.
            </Text>
            <Spacer height={10} />
            <Text opacity={0.96} style={{ maxWidth: 1045 }}>
              Currently, I'm focused on building{" "}
              <SpanRainbow bold>ML detection systems</SpanRainbow> to keep kids
              safe on{" "}
              <SpanPink onClick={() => window.open("https://roblox.com")}>
                Roblox
              </SpanPink>
              .
            </Text>
            <Spacer height={50} />
            <Divider />
          </TitleTextContainer>
        </TitleContainer>
        {!isMobile && windowSize.width > 1300 && <Image src={Semicircles} />}
      </NarrowRowContainer>
    </Row>
  );
};

const FeaturedProjectsContainer = styled(motion.div)`
  display: flex;
  flex: 7;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  transform: translateY(-30px);

  @media (max-width: 600px) {
    justify-content: flex-start;
    align-items: flex-start;
  }
`;

const FeaturedProjectsMenuContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const FeaturedRowContainer = styled(motion.div)`
  display: flex;
  flex-direction: row;
  justify-content: space-evenly;
  width: 85%;

  @media (max-width: 600px) {
    width: 100%;
    padding: 0 16px;
  }
`;

const FeaturedProjects = ({ windowSize }) => {
  const history = useHistory();
  return (
    <Row>
      <FeaturedRowContainer>
        {!isMobile && window.innerWidth > 1300 && (
          <>
            <Image src={Squares} />
            <div style={{ display: "flex", flex: 1 }} />
          </>
        )}
        <FeaturedProjectsContainer>
          <FeaturedProjectsMenuContainer>
            <Heading>Featured Projects</Heading>
            <Spacer height={50} />
            <FeaturedProjectsMenu
              colors={colors["top"]}
              windowSize={windowSize}
            />
            <Spacer height={30} />
            <Text>
              {"("}See the full list{" "}
              <SpanPink
                onClick={() => {
                  history.push("/projects");
                }}
              >
                here
              </SpanPink>
              {")"}
            </Text>
            <Spacer height={30} />
            <Divider />
          </FeaturedProjectsMenuContainer>
        </FeaturedProjectsContainer>
      </FeaturedRowContainer>
    </Row>
  );
};

const ExperiencesContainer = styled(motion.div)`
  display: flex;
  flex: 7;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  transform: translateY(-10px);

  @media (max-width: 600px) {
    justify-content: flex-start;
    align-items: flex-start;
  }
`;

const ExperiencesMenuContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const ExperienceRowContainer = styled(motion.div)`
  display: flex;
  flex-direction: row;
  justify-content: space-evenly;
  width: 85%;

  @media (max-width: 600px) {
    width: 100%;
    padding: 0 16px;
  }
`;

const Experiences = ({ windowSize }) => {
  return (
    <Row>
      <ExperienceRowContainer>
        <ExperiencesContainer>
          <ExperiencesMenuContainer>
            <Heading>Experience</Heading>
            <Spacer height={50} />
            <ExperienceMenu colors={colors["top"]} windowSize={windowSize} />
            <Spacer height={30} />
            <Text>
              {"("}See my resume{" "}
              <SpanPink onClick={() => window.open("/resume", "_blank")}>
                here
              </SpanPink>
              {")"}
            </Text>
            <Spacer height={30} />
            <Divider />
          </ExperiencesMenuContainer>
        </ExperiencesContainer>
        {!isMobile && window.innerWidth > 1300 && (
          <>
            <div style={{ display: "flex", flex: 1 }} />
            <Image src={Circles} />
          </>
        )}
      </ExperienceRowContainer>
    </Row>
  );
};

const FooterContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 30px;
  padding: 20px 0;

  @media (max-width: 600px) {
    gap: 20px;
    padding: 10px 0;
  }
`;

const FooterLinksRow = styled(motion.div)`
  display: flex;
  flex-direction: row;
  gap: 16px;

  @media (max-width: 600px) {
    gap: 12px;
  }
`;

const FooterLink = styled(motion.div)`
  font-family: Inter;
  font-weight: 600;
  font-size: 16px;
  color: white;
  cursor: pointer;
  padding: 10px 28px;
  border-radius: 100px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.05);
  transition: all 0.25s ease;
  opacity: 0.7;

  &:hover {
    opacity: 1;
    border-color: rgba(255, 255, 255, 0.35);
    background: rgba(255, 255, 255, 0.1);
    transform: translateY(-2px);
  }

  @media (max-width: 600px) {
    font-size: 14px;
    padding: 8px 20px;
  }
`;

const BackToTop = styled(motion.div)`
  font-family: Outfit;
  font-size: 14px;
  font-weight: 300;
  color: white;
  opacity: 0.35;
  cursor: pointer;
  transition: opacity 0.25s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    opacity: 0.7;
  }

  @media (max-width: 600px) {
    font-size: 13px;
  }
`;

const DisplayLinks = [
  { display: "Contact", uri: "/contact" },
  { display: "Resume", uri: "/resume" },
  { display: "Projects", uri: "/projects" },
];

const Links = () => {
  const history = useHistory();

  const scroll = () => {
    document
      .getElementById("scroller")
      .scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <FooterContainer>
      <FooterLinksRow>
        {DisplayLinks.map((link) => (
          <FooterLink
            key={link.display}
            onClick={() => {
              if (link.uri === "/resume") window.open(link.uri, "_blank");
              else history.push(link.uri);
            }}
          >
            {link.display}
          </FooterLink>
        ))}
      </FooterLinksRow>
      <BackToTop onClick={scroll}>
        <img src={UpIcon} style={{ width: 16, opacity: 0.5 }} alt="up" />
        Back to top
      </BackToTop>
    </FooterContainer>
  );
};

export { About };
